import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import {
  Card,
  RadioButtonGroup,
  Radio,
  MediaObject
} from '@salesforce/design-system-react';

import * as Util from '../../../common/utilities';

import { ExperimentAssetBrowser, ExperimentPerformanceChart } from '../';

import './ExperimentPerformanceChartDetails.scss';

class ExperimentPerformanceChartDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selection: [],
      chartToggle: 'clicks',
      sortColumn: { property: '', sortDirection: '' }
    };
  }

  componentDidMount() {
    const assets = this.assetPerformers();
    if (assets.length > 0 && this.state.selection.length === 0) {
      // Select the first 10 performers by default
      this.setState({
        selection: assets.slice(0, this.maxSelected())
      });
    }
  }

  componentDidUpdate(prevProps) {
    //utilpropchanged returns true if it changed
    if (Util.propChanged(this.props, prevProps, 'performance.performers')) {
      const assets = this.assetPerformers();
      if (assets.length > 0 && this.keysChanged(prevProps)) {
        // Select the top performers by default
        this.defaultSelection();
      }
    }
  }

  defaultSelection() {
    const assets = this.assetPerformers();
    if (assets.length > 0) {
      // Select the top performers by default
      this.setState({
        selection: assets.slice(0, this.maxSelected())
      });
    }
  }

  handleSort(sort) {
    this.setState({ sortColumn: { ...sort } });
    const { performers } = this.props.performance;
    const sortBy = sort.property;
    if (sort.sortDirection === 'desc') {
      performers.sort((a, b) => parseFloat(a[sortBy]) - parseFloat(b[sortBy]));
    } else {
      performers.sort((a, b) => parseFloat(b[sortBy]) - parseFloat(a[sortBy]));
    }
    // Select the top performers by default
    this.defaultSelection();
  }

  getI18nID(suffix) {
    return `components.experiment.performance.results.${suffix}`;
  }

  extractUuid(performer) {
    return Object.values(performer)[1];
  }

  keysChanged(prevProps) {
    let currentKeys = this.props.performance.performers.map(perf =>
      this.extractUuid(perf)
    );
    let prevKeys = prevProps.performance.performers.map(perf =>
      this.extractUuid(perf)
    );
    return prevKeys.length === 0
      ? true
      : currentKeys.length !== prevKeys.length ||
          !prevKeys.every(elem => currentKeys.indexOf(elem) > -1);
  }

  assetPerformers() {
    const performers =
      this.props.experiment?.status === 'DRAFT'
        ? this.props.experiment?.content_test_assets
        : this.props.performance.performers;
    return performers.map((performer, idx) => {
      const merged = {
        ...performer.selectable,
        ...performer,
        dot: idx + 1
      };

      delete merged.selectable;
      return merged;
    });
  }

  maxSelected() {
    return 10;
  }

  handleRowChanged = (event, { selection }) => {
    if (selection.length <= this.maxSelected() && selection.length > 0) {
      this.setState({ selection });
    }
  };

  header(empty) {
    const { formatMessage } = this.props.intl;
    const noDataText = formatMessage({
      id: 'components.ecs.analytics.perf.noData'
    });

    if (empty && !this.props.performance.filterWasModified) {
      return (
        <div className="performance-header-left">
          <span className="header">{noDataText}</span>
        </div>
      );
    }

    const performanceHeaderRight =
      this.props.experiment?.status === 'DRAFT' ? null : (
        <div className="performance-header-right">
          <div className="chart-toggle">
            <RadioButtonGroup
              className="chart-type-radio"
              onChange={event => {
                this.setState({ chartToggle: event.target.value });
              }}
            >
              <Radio
                key="ctor"
                id="ctor"
                labels={{
                  label: formatMessage({
                    id: this.getI18nID('clickChart.toggle.ctor')
                  })
                }}
                value="ctor"
                checked={this.state.chartToggle === 'ctor'}
                variant="button-group"
              />
              <Radio
                key="clicks"
                id="clicks"
                labels={{
                  label: formatMessage({
                    id: this.getI18nID('clickChart.toggle.clicks')
                  })
                }}
                value="clicks"
                checked={this.state.chartToggle === 'clicks'}
                variant="button-group"
              />
            </RadioButtonGroup>
          </div>
        </div>
      );

    return (
      <div className="performance-header slds-grid slds-grid_vertical-align-center">
        <div className="performance-header-left slds-col_bump-right">
          <span className="header">
            {formatMessage({
              id: this.getI18nID('chartTitle')
            })}
          </span>
        </div>
        {performanceHeaderRight}
      </div>
    );
  }

  columns(flags) {
    const isReadonly = flags?.isReadonly;
    const cols = [
      { name: 'thumbnail', sortable: false, width: '62px' },
      { name: 'name', sortable: false, width: '6.25rem' },
      {
        name: 'selections',
        sortable: true,
        numberStyle: { style: 'decimal' }
      },
      {
        name: 'clicks',
        sortable: true,
        numberStyle: { style: 'decimal' }
      },
      {
        name: 'ctor',
        sortable: true,
        numberStyle: { style: 'percent', maximumFractionDigits: 2 }
      }
    ];
    if (flags && !isReadonly && this.props.experiment?.status === 'PUBLISHED') {
      cols.push({ name: 'isDisabled', sortable: false });
    }
    return cols;
  }

  handleBrowserAction(session) {
    return (action, item, disabled) => {
      switch (action) {
        case 'enable':
          this.props.actions.activateSelectable(
            this.props.experiment,
            item,
            'analytics',
            { cToken: session.experimentCsrfToken }
          );
          break;
        case 'disable':
          this.props.actions.deactivateSelectable(
            this.props.experiment,
            item,
            'analytics',
            { cToken: session.experimentCsrfToken }
          );
          break;
        default:
          break;
      }
    };
  }

  trimDetails(details) {
    const firstData = details.findIndex(detail => detail?.data.length > 0);
    if (firstData > 0) {
      return details.slice(firstData - 1);
    } else {
      return details;
    }
  }

  render() {
    const { filter } = this.props;
    const isDraft = this.props.experiment?.status === 'DRAFT';
    const isHourly = filter.endDate - filter.startDate <= 172800000;
    const details = isHourly
      ? this.trimDetails(this.props.performance.details)
      : this.props.performance.details;
    const keys = [];
    const assets = this.assetPerformers();
    const selectedKeys = this.state.selection.map(selected =>
      this.extractUuid(selected)
    );
    const selectedPerformers = assets.filter(performer =>
      selectedKeys.includes(this.extractUuid(performer))
    );
    const count = this.state.selection.length || 0;
    keys.sort();

    const noDetails = (
      <div style={{ height: '50px', marginLeft: '1rem' }}>
        <FormattedMessage id="components.ecs.analytics.perf.noDataDetails" />
      </div>
    );

    const charts = (
      <React.Fragment>
        <div className="charts-card">
          <ExperimentPerformanceChart
            details={details}
            performers={selectedPerformers}
            filter={filter}
            chartToggle={this.state.chartToggle}
          />
        </div>
      </React.Fragment>
    );

    const assetComponents = (
      <React.Fragment>
        {isDraft ? null : charts}
        {isDraft ? null : (
          <div className="items-selected">
            <FormattedMessage
              id={this.getI18nID('items')}
              values={{ count, maxAssets: assets.length }}
            />
          </div>
        )}
        {isDraft ? (
          <ExperimentAssetBrowser
            assets={assets}
            columns={this.columns(this.props.flags)}
            showActions={false}
            popoverOverflow={assets.length < 12}
            className="highPerformers"
          />
        ) : (
          <ExperimentAssetBrowser
            assets={assets}
            experiment={this.props.experiment}
            columns={this.columns(this.props.flags)}
            onAction={this.handleBrowserAction(this.props.session)}
            showActions={
              this.props.experiment?.status === 'PUBLISHED' ? true : false
            }
            popoverOverflow={assets.length < 12}
            className="highPerformers"
            selection={selectedPerformers}
            rowSelectionMode="checkbox"
            onRowSelect={this.handleRowChanged}
            sortColumn={this.state.sortColumn}
            onSort={this.handleSort.bind(this)}
            sortable
          />
        )}
      </React.Fragment>
    );

    return (
      <div className="experiment-charts-card">
        <Card
          className="experiment-performance-details"
          header={<MediaObject body={this.header(!assets.length)} />}
        >
          {assets.length ? assetComponents : noDetails}
        </Card>
      </div>
    );
  }
}

ExperimentPerformanceChartDetails.propTypes = {
  actions: PropTypes.object,
  experiment: PropTypes.object,
  flags: PropTypes.object,
  performance: PropTypes.object,
  rules: PropTypes.object,
  filter: PropTypes.object.isRequired,
  location: PropTypes.object,
  session: PropTypes.object
};

export default injectIntl(ExperimentPerformanceChartDetails);
